import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Share2,
  Palette,
  BarChart2,
  Sparkles,
  Home,
  Loader2,
  Globe,
  X,
  Link as LinkIcon,
  CheckCircle2,
  Save,
  Upload,
  History,
  GraduationCap,
  Footprints,
  DollarSign,
  TrendingUp,
  TrendingDown,
  MapPin,
  ShieldAlert,
  RefreshCw,
  Flame,
  Droplets,
  Wind,
  Thermometer,
  Activity,
  Eye,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBuyerReportQuery,
  useBuyerReportUpdateMutation,
} from "@/hooks/useBuyerReportData";
import { useTeamAgents } from "@/hooks/useTeamAgents";
import { BuyerReportDocuments } from "@/components/BuyerReportDocuments";
import { cn } from "@/lib/utils";
import { getContrastColor } from "@/lib/colorUtils";

const VALID_SECTIONS = [
  "overview",
  "history",
  "schools",
  "walkability",
  "climate",
  "analysis",
  "offer",
  "design",
  "share",
] as const;
type Section = (typeof VALID_SECTIONS)[number];

const NAV_ITEMS: { id: Section; label: string; icon: React.ReactNode }[] = [
  { id: "overview", label: "Overview", icon: <Home className="h-4 w-4" /> },
  { id: "history", label: "History", icon: <History className="h-4 w-4" /> },
  { id: "schools", label: "Schools", icon: <GraduationCap className="h-4 w-4" /> },
  { id: "walkability", label: "Walkability", icon: <Footprints className="h-4 w-4" /> },
  { id: "climate", label: "Climate", icon: <Flame className="h-4 w-4" /> },
  { id: "analysis", label: "Analysis", icon: <Sparkles className="h-4 w-4" /> },
  { id: "offer", label: "Offer Strategy", icon: <DollarSign className="h-4 w-4" /> },
  { id: "design", label: "Design", icon: <Palette className="h-4 w-4" /> },
  { id: "share", label: "Share", icon: <Share2 className="h-4 w-4" /> },
];

function formatPrice(n: number | null | undefined) {
  if (!n) return "N/A";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function ScoreBadge({ score, label, description }: { score: number | null | undefined; label: string; description?: string }) {
  const val = score ?? 0;
  const hasScore = score != null;
  const color = !hasScore
    ? "bg-muted text-muted-foreground border-border"
    : val >= 70
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : val >= 50
    ? "bg-yellow-100 text-yellow-800 border-yellow-200"
    : "bg-red-100 text-red-800 border-red-200";
  return (
    <div className={`border rounded-xl p-4 text-center ${color}`}>
      <p className="text-3xl font-bold">{hasScore ? score : "N/A"}</p>
      <p className="text-sm font-medium mt-1">{label}</p>
      {description && <p className="text-xs mt-0.5 opacity-75">{description}</p>}
    </div>
  );
}

export default function BuyerReport() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeSection, setActiveSection] = useState<Section>("overview");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [isGeneratingOffer, setIsGeneratingOffer] = useState(false);
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [agentInsights, setAgentInsights] = useState("");
  const [isSavingInsights, setIsSavingInsights] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingHeadshot, setIsUploadingHeadshot] = useState(false);
  const [isUploadingFooterLogo, setIsUploadingFooterLogo] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");

  const { agents } = useTeamAgents();

  const [designEdits, setDesignEdits] = useState<{
    buyer_name?: string;
    custom_agent_name?: string;
    custom_agent_headshot_url?: string;
    custom_logo_url?: string;
    custom_footer_logo_url?: string;
    primary_brand_color?: string;
    secondary_brand_color?: string;
    agent_notes?: string;
  }>({});

  const { data: report, isLoading } = useBuyerReportQuery(id);
  const updateReport = useBuyerReportUpdateMutation(id!);

  const hoverColor = useMemo(() => {
    const base = report?.secondary_brand_color || report?.primary_brand_color || "#1b489b";
    const hex = base.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const wa = (255 * 50) / 100;
    const nr = Math.min(255, Math.round(r + wa));
    const ng = Math.min(255, Math.round(g + wa));
    const nb = Math.min(255, Math.round(b + wa));
    return `#${nr.toString(16).padStart(2, "0")}${ng.toString(16).padStart(2, "0")}${nb.toString(16).padStart(2, "0")}`;
  }, [report?.secondary_brand_color, report?.primary_brand_color]);

  // URL-sync for section tabs
  useEffect(() => {
    const section = searchParams.get("section") as Section | null;
    if (section && VALID_SECTIONS.includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  useEffect(() => {
    if (report) {
      setDesignEdits({
        buyer_name: report.buyer_name || "",
        custom_agent_name: report.custom_agent_name || "",
        custom_agent_headshot_url: report.custom_agent_headshot_url || "",
        custom_logo_url: report.custom_logo_url || "",
        custom_footer_logo_url: (report as any).custom_footer_logo_url || undefined,
        primary_brand_color: report.primary_brand_color || "#0c0f24",
        secondary_brand_color: report.secondary_brand_color || "#1b489b",
        agent_notes: report.agent_notes || "",
      });
      setAgentInsights((report as any).agent_offer_insights || "");
    }
  }, [report?.id]);

  // Auto-populate branding from team agent roster selection
  useEffect(() => {
    if (!selectedAgentId) return;
    const agent = agents.find((a) => a.id === selectedAgentId);
    if (!agent) return;
    setDesignEdits((prev) => ({
      ...prev,
      custom_agent_name: agent.name,
      custom_agent_headshot_url: agent.headshot_url || prev.custom_agent_headshot_url,
    }));
  }, [selectedAgentId, agents]);

  const handleSectionChange = (section: Section) => {
    setActiveSection(section);
    setSearchParams({ section }, { replace: true });
  };

  const handleRefreshData = async () => {
    if (!report?.zpid || !id) return;
    setIsRefreshing(true);
    try {
      const { error } = await supabase.functions.invoke("fetch-property-report-data", {
        body: { zpid: report.zpid, reportId: id },
      });
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["property-report", id] });
      toast({ title: "Data refreshed!", description: "Latest property data has been pulled." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Refresh failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePublishToggle = async () => {
    if (!report) return;
    setIsPublishing(true);
    try {
      await updateReport.mutateAsync({
        is_published: !report.is_published,
        published_at: !report.is_published ? new Date().toISOString() : null,
      } as any);
      toast({ title: report.is_published ? "Report Unpublished" : "Report Published" });
    } catch {
      toast({ title: "Error", description: "Failed to update publish status", variant: "destructive" });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!report?.is_published) {
      toast({ title: "Not published", description: "Publish the report first", variant: "destructive" });
      return;
    }
    const url = `${window.location.origin}/buyer-report/public/${report.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      toast({ title: "Link copied!" });
    } catch {
      toast({ title: "Error", description: "Failed to copy link", variant: "destructive" });
    }
  };

  const handleGenerateAnalysis = async () => {
    if (!id) return;
    setIsGeneratingAnalysis(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-property-analysis", {
        body: { reportId: id },
      });
      if (error) throw error;
      toast({ title: "Analysis generated!" });
      queryClient.invalidateQueries({ queryKey: ["property-report", id] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Generation failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsGeneratingAnalysis(false);
    }
  };

  const handleSaveInsights = async () => {
    if (!id) return;
    setIsSavingInsights(true);
    try {
      await updateReport.mutateAsync({ agent_offer_insights: agentInsights } as any);
      toast({ title: "Intel saved" });
    } catch {
      toast({ title: "Error", description: "Failed to save intel", variant: "destructive" });
    } finally {
      setIsSavingInsights(false);
    }
  };

  const handleGenerateOffer = async () => {
    if (!id) return;
    setIsGeneratingOffer(true);
    try {
      // Save latest insights to DB first so the edge function picks them up
      await updateReport.mutateAsync({ agent_offer_insights: agentInsights } as any);

      const { error } = await supabase.functions.invoke("generate-offer-strategy", {
        body: { reportId: id },
      });
      if (error) throw error;
      toast({ title: "Offer strategy generated!" });
      queryClient.invalidateQueries({ queryKey: ["property-report", id] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Generation failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsGeneratingOffer(false);
    }
  };

  const handleSaveDesign = async () => {
    if (!id) return;
    setIsSavingDesign(true);
    try {
      await updateReport.mutateAsync(designEdits as any);
      toast({ title: "Design saved" });
    } catch {
      toast({ title: "Error", description: "Failed to save design", variant: "destructive" });
    } finally {
      setIsSavingDesign(false);
    }
  };

  const handleUploadImage = async (
    file: File,
    field: "custom_logo_url" | "custom_agent_headshot_url" | "custom_footer_logo_url",
    setLoading: (v: boolean) => void
  ) => {
    setLoading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `property-reports/${id}/${field}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("logos")
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from("logos").getPublicUrl(path);
      setDesignEdits((prev) => ({ ...prev, [field]: publicUrl }));
      toast({ title: "Image uploaded" });
    } catch {
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-muted-foreground">Report not found</p>
        <Button variant="outline" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const pd = report.property_data || {};
  const fullAddress = [report.address, report.city, report.state, report.zip]
    .filter(Boolean)
    .join(", ");
  const listPrice = pd.price || pd.zestimate;
  const zestimate = pd.zestimate;
  const priceHistory: any[] = report.price_history || [];
  const taxHistory: any[] = report.tax_history || [];
  const schools: any[] = report.schools || [];
  const walkTransit = report.walk_transit_data || {};
  const analysis = report.ai_analysis_structured || {};
  const offerStrategy = report.ai_offer_strategy || {};
  const imageUrl =
    pd.desktopWebHdpImageLink ||
    pd.hiResImageLink ||
    pd.photos?.[0]?.mixedSources?.jpeg?.[0]?.url ||
    pd.photos?.[0]?.url ||
    pd.imgSrc;

  // Price delta: list vs Zestimate
  const priceDelta =
    listPrice && zestimate && listPrice !== zestimate
      ? ((listPrice - zestimate) / zestimate) * 100
      : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-20 bg-card shadow-sm">
        <div className="flex items-center justify-between px-3 md:px-6 py-3 border-b border-border">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} title="Dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-semibold text-sm truncate">{fullAddress}</h1>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Buyer Report</span>
                <Badge variant="secondary" className={`text-xs ${report.is_published ? "bg-green-600 text-white" : "bg-gray-600 text-white"}`}>
                  {report.is_published ? "Published" : "Draft"}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2">
            <Button
              variant="outline"
              size="sm"
              className="hidden md:flex gap-1.5"
              onClick={handleRefreshData}
              disabled={isRefreshing || !report?.zpid}
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePublishToggle}
              disabled={isPublishing}
              className="hidden md:flex gap-1.5"
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              {report.is_published ? "Unpublish" : "Publish"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCopyLink}
              disabled={!report.is_published}
              title="Copy public link"
            >
              {isCopied ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <LinkIcon className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-[57px] z-10 bg-card border-b border-border">
        <div className="flex items-center gap-1 px-3 md:px-6 py-2 overflow-x-auto scrollbar-hide">
          {NAV_ITEMS.map((item) => (
            <Button
              key={item.id}
              variant="ghost"
              size="sm"
              className={cn(
                "flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md",
                activeSection === item.id ? "bg-secondary text-secondary-foreground" : "text-muted-foreground"
              )}
              onClick={() => handleSectionChange(item.id)}
              onMouseEnter={(e) => {
                if (activeSection !== item.id) {
                  e.currentTarget.style.backgroundColor = hoverColor;
                  e.currentTarget.style.color = getContrastColor(hoverColor);
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== item.id) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "";
                }
              }}
            >
              {item.icon}
              <span className="text-sm font-medium">{item.label}</span>
            </Button>
          ))}
        </div>
      </div>

      <main className="flex-1 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4 py-6">
        {/* ─── Overview ─── */}
        {activeSection === "overview" && (
          <div className="space-y-6">
            {imageUrl && (
              <div className="w-full aspect-[16/7] rounded-xl overflow-hidden bg-muted">
                <img src={imageUrl} alt={fullAddress} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold">{formatPrice(listPrice)}</p>
                  <p className="text-xs text-muted-foreground mt-1">List Price</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold">{formatPrice(zestimate)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Zestimate</p>
                </CardContent>
              </Card>
              {priceDelta !== null && (
                <Card>
                  <CardContent className="pt-4 pb-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {priceDelta > 0 ? (
                        <TrendingUp className="h-4 w-4 text-red-500" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-emerald-500" />
                      )}
                      <p className={cn("text-2xl font-bold", priceDelta > 0 ? "text-red-600" : "text-emerald-600")}>
                        {priceDelta > 0 ? "+" : ""}{priceDelta.toFixed(1)}%
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">vs Zestimate</p>
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold">{pd.daysOnZillow ?? pd.timeOnZillow ?? "N/A"}</p>
                  <p className="text-xs text-muted-foreground mt-1">Days on Market</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Property Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Bedrooms</dt>
                    <dd className="font-medium">{pd.bedrooms ?? "N/A"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Bathrooms</dt>
                    <dd className="font-medium">{pd.bathrooms ?? "N/A"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Square Feet</dt>
                    <dd className="font-medium">{pd.livingArea?.toLocaleString() ?? "N/A"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Year Built</dt>
                    <dd className="font-medium">{pd.yearBuilt ?? "N/A"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Property Type</dt>
                    <dd className="font-medium">{pd.homeType ?? "N/A"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Lot Size</dt>
                    <dd className="font-medium">
                      {pd.lotAreaValue ? `${pd.lotAreaValue.toLocaleString()} ${pd.lotAreaUnit || "sqft"}` : "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Flood Zone</dt>
                    <dd className="font-medium flex items-center gap-1">
                      {report.fema_flood_zone || "N/A"}
                      {report.fema_flood_zone && report.fema_flood_zone !== "X" && (
                        <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
                      )}
                    </dd>
                  </div>
                  {report.zpid && (
                    <div>
                      <dt className="text-muted-foreground">Zillow</dt>
                      <dd>
                        <a
                          href={report.zillow_url || `https://www.zillow.com/homedetails/${report.zpid}_zpid/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-xs"
                        >
                          View on Zillow
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>

            {pd.description && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">{pd.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ─── History ─── */}
        {activeSection === "history" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Price History</CardTitle>
              </CardHeader>
              <CardContent>
                {priceHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No price history available.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-muted-foreground font-medium">Date</th>
                        <th className="text-left py-2 text-muted-foreground font-medium">Event</th>
                        <th className="text-right py-2 text-muted-foreground font-medium">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceHistory.map((p: any, i: number) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2">{p.date || "N/A"}</td>
                          <td className="py-2 text-muted-foreground">{p.event || p.priceChangeRate != null ? "Price Change" : "Listed"}</td>
                          <td className="py-2 text-right font-medium">
                            {p.price ? `$${p.price.toLocaleString()}` : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tax History</CardTitle>
              </CardHeader>
              <CardContent>
                {taxHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tax history available.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-muted-foreground font-medium">Year</th>
                        <th className="text-right py-2 text-muted-foreground font-medium">Tax Paid</th>
                        <th className="text-right py-2 text-muted-foreground font-medium">Assessed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxHistory.map((t: any, i: number) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2">{t.time ? new Date(t.time).getFullYear() : t.year || "N/A"}</td>
                          <td className="py-2 text-right font-medium">
                            {t.taxPaid ? `$${t.taxPaid.toLocaleString()}` : "N/A"}
                          </td>
                          <td className="py-2 text-right text-muted-foreground">
                            {t.value ? `$${t.value.toLocaleString()}` : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Schools ─── */}
        {activeSection === "schools" && (
          <div className="space-y-4">
            {schools.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">No school data available for this property.</p>
                </CardContent>
              </Card>
            ) : (
              schools.map((school: any, i: number) => (
                <Card key={i}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{school.name || "Unknown School"}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {school.type || "School"}
                          {school.grades && ` · Grades ${school.grades}`}
                          {school.distance && ` · ${school.distance} mi away`}
                        </p>
                      </div>
                      {school.rating && (
                        <div className={cn(
                          "flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg",
                          school.rating >= 8
                            ? "bg-emerald-100 text-emerald-800"
                            : school.rating >= 5
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        )}>
                          {school.rating}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* ─── Walkability ─── */}
        {activeSection === "walkability" && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <ScoreBadge
                score={walkTransit.walkScore?.walkscore}
                description={walkTransit.walkScore?.description}
                label="Walk Score"
              />
              <ScoreBadge
                score={walkTransit.transitScore?.transitscore ?? (walkTransit.transitScore === null ? undefined : walkTransit.transitScore?.score)}
                description={walkTransit.transitScore?.description}
                label="Transit Score"
              />
              <ScoreBadge
                score={walkTransit.bikeScore?.bikescore}
                description={walkTransit.bikeScore?.description}
                label="Bike Score"
              />
            </div>

            {!walkTransit.walkScore?.walkscore && !walkTransit.bikeScore?.bikescore && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">
                    Walk/transit scores not yet fetched. Run "Fetch Property Data" to get scores.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ─── Climate & Risks ─── */}
        {activeSection === "climate" && (() => {
          const climate = pd.climate;
          if (!climate) return (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Climate data not available. Click Refresh to fetch it.</p>
              </CardContent>
            </Card>
          );

          function getRiskColors(value: number) {
            if (value <= 2) return { card: "border-emerald-200 bg-emerald-50", badge: "bg-emerald-100 text-emerald-800", bar: "bg-emerald-500" };
            if (value <= 4) return { card: "border-yellow-200 bg-yellow-50", badge: "bg-yellow-100 text-yellow-800", bar: "bg-yellow-500" };
            if (value <= 6) return { card: "border-orange-200 bg-orange-50", badge: "bg-orange-100 text-orange-800", bar: "bg-orange-500" };
            return { card: "border-red-200 bg-red-50", badge: "bg-red-100 text-red-800", bar: "bg-red-500" };
          }

          function insuranceLabel(rec: string | undefined) {
            if (!rec) return null;
            const map: Record<string, string> = {
              NOT_CRITICAL: "Not required",
              REQUIRED: "Required",
              RECOMMENDED: "Recommended",
              LIKELY_OPTIONAL: "Likely optional",
            };
            return map[rec] ?? rec;
          }

          const risks = [
            {
              key: "flood",
              label: "Flood",
              icon: <Droplets className="h-5 w-5" />,
              source: climate.floodSources?.primary,
              extra: climate.floodSources?.primary?.femaZone
                ? `FEMA Zone: ${climate.floodSources.primary.femaZone.replace("_UNSHADED", "").replace("_SHADED", " (Shaded)")}`
                : null,
            },
            {
              key: "fire",
              label: "Fire",
              icon: <Flame className="h-5 w-5" />,
              source: climate.fireSources?.primary,
              extra: climate.fireSources?.primary?.historicCountAll != null
                ? `${climate.fireSources.primary.historicCountAll} historical fire event(s) nearby`
                : null,
            },
            {
              key: "wind",
              label: "Wind",
              icon: <Wind className="h-5 w-5" />,
              source: climate.windSources?.primary,
              extra: climate.windSources?.primary?.historicCountAll != null
                ? `${climate.windSources.primary.historicCountAll} historical wind event(s) nearby`
                : null,
            },
            {
              key: "air",
              label: "Air Quality",
              icon: <Activity className="h-5 w-5" />,
              source: climate.airSources?.primary,
              extra: (() => {
                const now = climate.airSources?.primary?.badAirDays?.find((d: any) => d.relativeYear === 0)?.dayCount;
                const yr30 = climate.airSources?.primary?.badAirDays?.find((d: any) => d.relativeYear === 30)?.dayCount;
                if (now == null) return null;
                return `~${now} bad air day${now !== 1 ? "s" : ""}/yr now · ~${yr30 ?? "?"} in 30 yrs`;
              })(),
            },
            {
              key: "heat",
              label: "Heat",
              icon: <Thermometer className="h-5 w-5" />,
              source: climate.heatSources?.primary,
              extra: (() => {
                const now = climate.heatSources?.primary?.hotDays?.find((d: any) => d.relativeYear === 0)?.dayCount;
                const yr30 = climate.heatSources?.primary?.hotDays?.find((d: any) => d.relativeYear === 30)?.dayCount;
                const temp = climate.heatSources?.primary?.percentile98Temp;
                if (now == null) return null;
                return `~${now} extreme heat day${now !== 1 ? "s" : ""}/yr now · ~${yr30 ?? "?"} in 30 yrs${temp ? ` · Max ~${temp}°F` : ""}`;
              })(),
            },
          ];

          return (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold">Climate & Environmental Risk</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Sourced from First Street Foundation via Zillow</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {risks.map(({ key, label, icon, source, extra }) => {
                  const score = source?.riskScore;
                  if (!score) return null;
                  const colors = getRiskColors(score.value);
                  const ins = insuranceLabel(source?.insuranceRecommendation);
                  const pct = Math.round((score.value / (score.max || 10)) * 100);
                  return (
                    <div key={key} className={`rounded-xl border p-4 ${colors.card}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {icon}
                          <span className="font-medium text-sm">{label}</span>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
                          {score.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-1.5 bg-black/10 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${colors.bar}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-medium tabular-nums">{score.value}/{score.max}</span>
                      </div>
                      {ins && <p className="text-xs opacity-70">Insurance: {ins}</p>}
                      {extra && <p className="text-xs opacity-70 mt-0.5">{extra}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ─── Analysis ─── */}
        {activeSection === "analysis" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold">AI Market Analysis</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Buyer-perspective assessment of this property</p>
              </div>
              <Button
                onClick={handleGenerateAnalysis}
                disabled={isGeneratingAnalysis}
                size="sm"
                className="gap-2"
              >
                {isGeneratingAnalysis ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                {analysis.marketSummary ? "Regenerate" : "Generate Analysis"}
              </Button>
            </div>

            {analysis.marketSummary ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Market Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">{analysis.marketSummary}</p>
                  </CardContent>
                </Card>

                {analysis.priceAssessment && (
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <Badge
                          variant="outline"
                          className={cn(
                            "capitalize text-sm px-3 py-1",
                            analysis.priceAssessment === "fair"
                              ? "text-emerald-700 border-emerald-300 bg-emerald-50"
                              : analysis.priceAssessment === "overpriced"
                              ? "text-red-700 border-red-300 bg-red-50"
                              : "text-blue-700 border-blue-300 bg-blue-50"
                          )}
                        >
                          {analysis.priceAssessment}
                        </Badge>
                        <p className="text-sm text-muted-foreground">{analysis.priceAssessmentDetail}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {analysis.keyFindings?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Key Findings</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {analysis.keyFindings.map((f: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

              </div>
            ) : (
              <Card>
                <CardContent className="pt-8 pb-8 text-center">
                  <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    No analysis yet. Click "Generate Analysis" to create a buyer-perspective market assessment.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ─── Offer Strategy ─── */}
        {activeSection === "offer" && (
          <div className="space-y-5">
            {/* Agent Intel */}
            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-600" />
                  Agent Intel
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Share what you know — multiple offers, seller motivation, inspection concerns, timeline pressure. This gets sent directly to the AI.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  placeholder={"e.g. \"Multiple offers situation, seller reviewing Sunday\" or \"Seller needs to close by May 1st\" or \"Foundation concern from walkthrough — may need inspection contingency\""}
                  value={agentInsights}
                  onChange={(e) => setAgentInsights(e.target.value)}
                  className="min-h-[100px] resize-none bg-white text-sm"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {agentInsights.trim() ? "Intel will be included in the next generation." : "No intel added yet."}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveInsights}
                    disabled={isSavingInsights}
                    className="gap-1.5 h-7 text-xs"
                  >
                    {isSavingInsights ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Save Intel
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold">Offer Strategy</h2>
                <p className="text-xs text-muted-foreground mt-0.5">AI-generated offer recommendation and negotiation tactics</p>
              </div>
              <Button
                onClick={handleGenerateOffer}
                disabled={isGeneratingOffer}
                size="sm"
                className="gap-2"
              >
                {isGeneratingOffer ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <DollarSign className="h-3.5 w-3.5" />
                )}
                {offerStrategy.suggestedOffer ? "Regenerate" : "Generate Strategy"}
              </Button>
            </div>

            {offerStrategy.suggestedOffer ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4 pb-3 text-center">
                      <p className="text-xl font-bold text-blue-600">{formatPrice(offerStrategy.offerLow)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Conservative</p>
                    </CardContent>
                  </Card>
                  <Card className="ring-2 ring-primary">
                    <CardContent className="pt-4 pb-3 text-center">
                      <p className="text-xl font-bold text-primary">{formatPrice(offerStrategy.suggestedOffer)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Recommended</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3 text-center">
                      <p className="text-xl font-bold text-emerald-600">{formatPrice(offerStrategy.offerHigh)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Competitive</p>
                    </CardContent>
                  </Card>
                </div>

                {offerStrategy.offerRationale && (
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">{offerStrategy.offerRationale}</p>
                    </CardContent>
                  </Card>
                )}

                {offerStrategy.tactics?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Negotiation Tactics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {offerStrategy.tactics.map((t: string, i: number) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {offerStrategy.contingencyAdvice && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Contingency Advice</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">{offerStrategy.contingencyAdvice}</p>
                    </CardContent>
                  </Card>
                )}

                {offerStrategy.negotiationNotes && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Negotiation Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">{offerStrategy.negotiationNotes}</p>
                      {offerStrategy.urgencyLevel && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground">Urgency:</span>
                          <Badge
                            variant="outline"
                            className={cn(
                              "capitalize text-xs",
                              offerStrategy.urgencyLevel === "high"
                                ? "text-red-700 border-red-300"
                                : offerStrategy.urgencyLevel === "medium"
                                ? "text-yellow-700 border-yellow-300"
                                : "text-emerald-700 border-emerald-300"
                            )}
                          >
                            {offerStrategy.urgencyLevel}
                          </Badge>
                          {offerStrategy.urgencyExplanation && (
                            <span className="text-xs text-muted-foreground">{offerStrategy.urgencyExplanation}</span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="pt-8 pb-8 text-center">
                  <DollarSign className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    No offer strategy yet. Click "Generate Strategy" to get an AI-powered offer recommendation.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ─── Design ─── */}
        {activeSection === "design" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">Report Design</h2>
              <Button
                onClick={handleSaveDesign}
                disabled={isSavingDesign}
                size="sm"
                className="gap-2"
              >
                {isSavingDesign ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Save
              </Button>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Buyer Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="buyer_name">Buyer Name</Label>
                  <Input
                    id="buyer_name"
                    value={designEdits.buyer_name || ""}
                    onChange={(e) => setDesignEdits((p) => ({ ...p, buyer_name: e.target.value }))}
                    placeholder="John & Jane Doe"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="agent_notes">Agent Notes (shown on report)</Label>
                  <Textarea
                    id="agent_notes"
                    value={designEdits.agent_notes || ""}
                    onChange={(e) => setDesignEdits((p) => ({ ...p, agent_notes: e.target.value }))}
                    placeholder="Add context, advice, or notes for your buyer..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Agent Branding</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Agent selector — auto-populates name + headshot from team roster */}
                {agents.length > 0 && (
                  <div>
                    <Label>Select from Team Roster</Label>
                    <Select
                      value={selectedAgentId}
                      onValueChange={(v) => setSelectedAgentId(v === "none" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="— Enter manually —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Enter manually —</SelectItem>
                        {agents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            <div className="flex items-center gap-2">
                              {agent.headshot_url && (
                                <img
                                  src={agent.headshot_url}
                                  alt={agent.name}
                                  className="h-5 w-5 rounded-full object-cover"
                                />
                              )}
                              {agent.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Selecting an agent auto-fills name and headshot. You can still edit manually.
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="custom_agent_name">Agent Name</Label>
                  <Input
                    id="custom_agent_name"
                    value={designEdits.custom_agent_name || ""}
                    onChange={(e) => setDesignEdits((p) => ({ ...p, custom_agent_name: e.target.value }))}
                    placeholder="Your name"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Agent Headshot</Label>
                  <div className="mt-1 flex items-center gap-3">
                    {designEdits.custom_agent_headshot_url && (
                      <img
                        src={designEdits.custom_agent_headshot_url}
                        alt="Headshot"
                        className="h-12 w-12 rounded-full object-cover border"
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      disabled={isUploadingHeadshot}
                      onClick={() => document.getElementById("headshot-upload")?.click()}
                    >
                      {isUploadingHeadshot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      Upload Photo
                    </Button>
                    {designEdits.custom_agent_headshot_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={() => setDesignEdits((p) => ({ ...p, custom_agent_headshot_url: "" }))}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <input
                      id="headshot-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadImage(file, "custom_agent_headshot_url", setIsUploadingHeadshot);
                      }}
                    />
                  </div>
                </div>

                <div>
                  <Label>Header Logo <span className="text-muted-foreground font-normal text-xs">(shown in report hero)</span></Label>
                  <div className="mt-1 flex items-center gap-3">
                    {designEdits.custom_logo_url && (
                      <img
                        src={designEdits.custom_logo_url}
                        alt="Header Logo"
                        className="h-8 max-w-[120px] object-contain border rounded"
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      disabled={isUploadingLogo}
                      onClick={() => document.getElementById("logo-upload")?.click()}
                    >
                      {isUploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      Upload
                    </Button>
                    {designEdits.custom_logo_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={() => setDesignEdits((p) => ({ ...p, custom_logo_url: "" }))}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadImage(file, "custom_logo_url", setIsUploadingLogo);
                      }}
                    />
                  </div>
                </div>

                <div>
                  <Label>Footer Logo <span className="text-muted-foreground font-normal text-xs">(shown next to agent in footer)</span></Label>
                  <div className="mt-1 flex items-center gap-3">
                    {designEdits.custom_footer_logo_url && (
                      <img
                        src={designEdits.custom_footer_logo_url}
                        alt="Footer Logo"
                        className="h-8 max-w-[120px] object-contain border rounded"
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      disabled={isUploadingFooterLogo}
                      onClick={() => document.getElementById("footer-logo-upload")?.click()}
                    >
                      {isUploadingFooterLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      Upload
                    </Button>
                    {designEdits.custom_footer_logo_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={() => setDesignEdits((p) => ({ ...p, custom_footer_logo_url: "" }))}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <input
                      id="footer-logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadImage(file, "custom_footer_logo_url", setIsUploadingFooterLogo);
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Brand Colors</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="primary_color">Primary Color</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      id="primary_color"
                      value={designEdits.primary_brand_color || "#0c0f24"}
                      onChange={(e) => setDesignEdits((p) => ({ ...p, primary_brand_color: e.target.value }))}
                      className="h-9 w-14 rounded border cursor-pointer"
                    />
                    <Input
                      value={designEdits.primary_brand_color || "#0c0f24"}
                      onChange={(e) => setDesignEdits((p) => ({ ...p, primary_brand_color: e.target.value }))}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="secondary_color">Secondary Color</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      id="secondary_color"
                      value={designEdits.secondary_brand_color || "#1b489b"}
                      onChange={(e) => setDesignEdits((p) => ({ ...p, secondary_brand_color: e.target.value }))}
                      className="h-9 w-14 rounded border cursor-pointer"
                    />
                    <Input
                      value={designEdits.secondary_brand_color || "#1b489b"}
                      onChange={(e) => setDesignEdits((p) => ({ ...p, secondary_brand_color: e.target.value }))}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Share ─── */}
        {activeSection === "share" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Share with Buyer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Publish Report</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {report.is_published
                        ? "Report is live and accessible via the link below"
                        : "Enable to share a public link with your buyer"}
                    </p>
                  </div>
                  <Button
                    variant={report.is_published ? "outline" : "default"}
                    size="sm"
                    onClick={handlePublishToggle}
                    disabled={isPublishing}
                    className="gap-2"
                  >
                    {isPublishing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : report.is_published ? (
                      <Globe className="h-4 w-4 text-green-600" />
                    ) : (
                      <Globe className="h-4 w-4" />
                    )}
                    {report.is_published ? "Unpublish" : "Publish"}
                  </Button>
                </div>

                {report.is_published && (
                  <div className="rounded-lg bg-muted p-3 flex items-center gap-3">
                    <p className="text-sm font-mono truncate flex-1 text-muted-foreground">
                      {window.location.origin}/buyer-report/public/{report.id}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyLink}
                      className="gap-2 shrink-0"
                    >
                      {isCopied ? (
                        <><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Copied</>
                      ) : (
                        <><LinkIcon className="h-3.5 w-3.5" /> Copy Link</>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <BuyerReportEmailShare
              reportId={report.id}
              isPublished={report.is_published}
              publicUrl={`${window.location.origin}/buyer-report/public/${report.id}`}
              address={report.address}
              agentName={report.custom_agent_name || null}
              agentHeadshotUrl={report.custom_agent_headshot_url || null}
              logoUrl={report.custom_logo_url || null}
              primaryColor={report.primary_brand_color || "#0c0f24"}
              buyerName={report.buyer_name || null}
              propertyImageUrl={
                (report.property_data as any)?.desktopWebHdpImageLink ||
                (report.property_data as any)?.hiResImageLink ||
                (report.property_data as any)?.imgSrc ||
                null
              }
            />

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload files or add links (Google Drive, Canva, etc.) for your buyer to access.
                </p>
                <BuyerReportDocuments reportId={report.id} teamId={report.team_id} readOnly={false} />
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </main>
    </div>
  );
}

function BuyerReportEmailShare({
  reportId,
  isPublished,
  publicUrl,
  address,
  agentName,
  agentHeadshotUrl,
  logoUrl,
  primaryColor,
  buyerName,
  propertyImageUrl,
}: {
  reportId: string;
  isPublished: boolean;
  publicUrl: string;
  address: string;
  agentName: string | null;
  agentHeadshotUrl: string | null;
  logoUrl: string | null;
  primaryColor: string;
  buyerName: string | null;
  propertyImageUrl: string | null;
}) {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const brandColor = primaryColor || "#0c0f24";
  const northLogoUrl = "https://zkqcqgjbsigjdwzmnwmp.supabase.co/storage/v1/object/public/logos/north-logo.png";

  const buildHtmlBody = (greeting: string) => `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;padding:32px 16px;">
  <tr><td align="center">
    <table width="100%" cellpadding="0" cellspacing="0" style="max-width:580px;">

      <!-- Logo row -->
      ${logoUrl ? `
      <tr><td style="background:#ffffff;border-radius:12px 12px 0 0;padding:24px 32px;text-align:center;">
        <img src="${logoUrl}" alt="" style="max-height:40px;max-width:160px;object-fit:contain;display:inline-block;">
      </td></tr>` : ""}

      ${propertyImageUrl ? `
      <!-- Property hero image -->
      <tr><td style="padding:0;line-height:0;">
        <img src="${propertyImageUrl}" alt="${address}"
             style="width:100%;max-height:280px;object-fit:cover;display:block;${!logoUrl ? "border-radius:12px 12px 0 0;" : ""}">
      </td></tr>` : ""}

      <!-- Body card -->
      <tr><td style="background:#ffffff;padding:40px 40px 32px;${!propertyImageUrl && !logoUrl ? "border-radius:12px 12px 0 0;" : ""}">

        <!-- Heading -->
        <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;line-height:1.3;">
          Know Before You Offer
        </h1>
        <p style="margin:0 0 24px;font-size:15px;color:#64748b;">
          ${address}
        </p>

        <!-- Greeting -->
        <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
          Hi${greeting ? ` ${greeting}` : ""},
        </p>
        <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
          I've put together a detailed property report for this home. Inside you'll find price history, tax records, school ratings, walkability scores, climate risk data, and an AI-powered market analysis to help you make a confident decision.
        </p>

        <!-- CTA button -->
        <table width="100%" cellpadding="0" cellspacing="0" style="margin:28px 0 24px;">
          <tr><td align="center">
            <a href="${publicUrl}"
               style="display:inline-block;background:${brandColor};color:#ffffff;font-size:16px;font-weight:600;
                      text-decoration:none;padding:14px 36px;border-radius:8px;letter-spacing:0.01em;">
              View Property Report →
            </a>
          </td></tr>
        </table>

        <p style="margin:0 0 32px;font-size:13px;color:#94a3b8;text-align:center;">
          Or copy this link:<br>
          <a href="${publicUrl}" style="color:${brandColor};word-break:break-all;">${publicUrl}</a>
        </p>

        <!-- Divider -->
        <hr style="border:none;border-top:1px solid #e2e8f0;margin:0 0 24px;">

        <!-- Agent signature -->
        <table cellpadding="0" cellspacing="0">
          <tr>
            ${agentHeadshotUrl ? `
            <td style="padding-right:16px;vertical-align:top;">
              <img src="${agentHeadshotUrl}" alt="${agentName || ""}"
                   style="width:56px;height:56px;border-radius:50%;object-fit:cover;display:block;">
            </td>` : ""}
            <td style="vertical-align:middle;">
              <p style="margin:0 0 2px;font-size:15px;font-weight:600;color:#0f172a;">${agentName || "Your Agent"}</p>
            </td>
          </tr>
        </table>

      </td></tr>

      <!-- Footer -->
      <tr><td style="background:#f8fafc;border-top:1px solid #e2e8f0;border-radius:0 0 12px 12px;padding:16px 32px;text-align:center;">
        <p style="margin:0 0 6px;font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.08em;">Powered by</p>
        <img src="${northLogoUrl}" alt="North" style="max-height:24px;max-width:80px;object-fit:contain;display:inline-block;opacity:0.6;">
        <p style="margin:8px 0 0;font-size:11px;color:#94a3b8;">You received this because your agent shared a property report with you.</p>
      </td></tr>

    </table>
  </td></tr>
</table>
</body>
</html>`;

  const handleSend = async () => {
    if (!email.trim()) {
      toast({ title: "Email is required", variant: "destructive" });
      return;
    }
    if (!isPublished) {
      toast({ title: "Publish first", description: "Publish the report before sending via email.", variant: "destructive" });
      return;
    }

    setIsSending(true);
    try {
      const greeting = name || buyerName || "";
      const htmlBody = buildHtmlBody(greeting);

      const { error } = await supabase.functions.invoke("send-buyer-report-email", {
        body: {
          reportId,
          recipientEmail: email.trim(),
          recipientName: name.trim() || undefined,
          subject: `Know Before You Offer: ${address}`,
          htmlBody,
        },
      });

      if (error) throw error;

      toast({ title: "Email sent!", description: `Report sent to ${email}` });
      setEmail("");
      setName("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send email";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  const previewHtml = buildHtmlBody(buyerName || "");

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Email to Buyer</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-xs"
            onClick={() => setShowPreview((v) => !v)}
          >
            <Eye className="h-3.5 w-3.5" />
            {showPreview ? "Hide Preview" : "Preview Email"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {showPreview && (
          <div className="rounded-lg border border-border overflow-hidden bg-muted">
            <p className="text-xs text-muted-foreground px-3 py-2 border-b border-border bg-background">
              Email Preview
            </p>
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="w-full border-0"
              style={{ height: "520px" }}
              sandbox="allow-same-origin"
            />
          </div>
        )}
        {!isPublished && (
          <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
            Publish the report first to send via email.
          </p>
        )}
        <div>
          <Label>Recipient Name (optional)</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
          />
        </div>
        <div>
          <Label>Recipient Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="buyer@example.com"
          />
        </div>
        <Button onClick={handleSend} disabled={isSending || !isPublished} className="gap-2">
          {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
          {isSending ? "Sending..." : "Send Email"}
        </Button>
      </CardContent>
    </Card>
  );
}
